import { useState, useMemo } from "react"
import {
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Button,
  IconButton,
  Tooltip,
} from "@mui/material"
import {
  Assignment as AssignmentIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  CalendarToday as CalendarIcon,
  Refresh as RefreshIcon,
  Assessment as AssessmentIcon,
  Group,
} from "@mui/icons-material"
import { useApiQuery } from "../../hooks/useApi"
import ReportGenerator from "./ReportGenerator"

function StatCard({ title, value, icon, color = "primary", subtitle }) {
  return (
    <Card sx={{ height: "100%" }}>
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box>
            <Typography color="textSecondary" gutterBottom variant="overline">
              {title}
            </Typography>
            <Typography variant="h4" component="h2">
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="textSecondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box sx={{ color: `${color}.main` }}>{icon}</Box>
        </Box>
      </CardContent>
    </Card>
  )
}

function NormalDashboard() {
  const [dateFilter, setDateFilter] = useState("today")
  const [reportDialogOpen, setReportDialogOpen] = useState(false)

  const { data: userStats, isLoading, refetch } = useApiQuery(["user-stats"], "/users/stats")

  // Filter jobs based on date
  const getFilteredStats = useMemo(() => {
    if (!userStats) return null

    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1)

    const filterByDate = (jobs) => {
      if (!jobs || !Array.isArray(jobs)) return []
      
      return jobs.filter((job) => {
        const jobDate = new Date(job.createdAt || job.dueDate)

        switch (dateFilter) {
          case "today":
            return jobDate >= today
          case "30days":
            return jobDate >= thirtyDaysAgo
          case "month":
            return jobDate >= monthStart
          case "all":
          default:
            return true
        }
      })
    }

    // Filter all job arrays
    const filteredRecentJobs = filterByDate(userStats.recentJobs || [])
    
    // Recalculate stats based on filtered data
    const newJobs = filterByDate(userStats.allJobs?.filter(j => j.status === "new") || []).length
    const completedJobs = filterByDate(userStats.allJobs?.filter(j => j.status === "completed") || []).length
    const cancelledJobs = filterByDate(userStats.allJobs?.filter(j => j.status === "cancelled") || []).length
    
    // Recalculate processing breakdown
    const processingJobsFiltered = filterByDate(userStats.allJobs?.filter(j => j.status === "processing") || [])
    const processingJobs = {
      accepted: processingJobsFiltered.filter(j => j.subStatus === "accepted" || !j.subStatus).length,
      documentsPending: processingJobsFiltered.filter(j => j.subStatus === "documentsPending").length,
      taxPending: processingJobsFiltered.filter(j => j.subStatus === "taxPending").length,
      verificationPending: processingJobsFiltered.filter(j => j.subStatus === "verificationPending").length,
      clientNotResponding: processingJobsFiltered.filter(j => j.subStatus === "clientNotResponding").length,
      onHold: processingJobsFiltered.filter(j => j.subStatus === "keptOnHold").length,
    }

    return {
      newJobs,
      processingJobs,
      completedJobs,
      cancelledJobs,
      recentJobs: filteredRecentJobs.slice(0, 10),
    }
  }, [userStats, dateFilter])

  const displayStats = getFilteredStats || {
    newJobs: 0,
    processingJobs: {
      accepted: 0,
      documentsPending: 0,
      taxPending: 0,
      verificationPending: 0,
      clientNotResponding: 0,
      onHold: 0,
    },
    completedJobs: 0,
    cancelledJobs: 0,
    recentJobs: [],
  }

  const totalProcessing = Object.values(displayStats.processingJobs).reduce((sum, count) => sum + count, 0)

  const getStatusColor = (status) => {
    const statusColors = {
      "Documents Pending": "warning",
      "documentsPending": "warning",
      "Tax Pending": "info",
      "taxPending": "info",
      "Accepted": "success",
      "accepted": "success",
      "Verification Pending": "secondary",
      "verificationPending": "secondary",
      "Client Not Responding": "error",
      "clientNotResponding": "error",
      "Kept on Hold": "default",
      "keptOnHold": "default",
      "completed": "success",
      "processing": "primary",
      "new": "info",
      "cancelled": "error",
    }
    return statusColors[status] || "default"
  }

  const getFilterLabel = () => {
    switch (dateFilter) {
      case "today":
        return "Today"
      case "30days":
        return "Last 30 Days"
      case "month":
        return "This Month"
      case "all":
        return "All Time"
      default:
        return "This Month"
    }
  }

  const getSubStatusLabel = (subStatus) => {
    const labels = {
      accepted: "Accepted",
      documentsPending: "Documents Pending",
      taxPending: "Tax Pending",
      verificationPending: "Verification Pending",
      clientNotResponding: "Client Not Responding",
      keptOnHold: "Kept on Hold",
    }
    return labels[subStatus] || subStatus
  }

  if (isLoading) return <Typography>Loading...</Typography>

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            My Dashboard
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Welcome back! Here's an overview of your current workload.
          </Typography>
        </Box>
        <Stack direction="row" spacing={2} alignItems="center">
          <CalendarIcon color="action" />
          <Typography variant="body1" fontWeight={600}>
            Filter Period:
          </Typography>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Time Period</InputLabel>
            <Select value={dateFilter} label="Time Period" onChange={(e) => setDateFilter(e.target.value)}>
              <MenuItem value="today">Today</MenuItem>
              <MenuItem value="30days">Last 30 Days</MenuItem>
              <MenuItem value="month">This Month</MenuItem>
              <MenuItem value="all">All Time</MenuItem>
            </Select>
          </FormControl>
          <Typography variant="body2" color="textSecondary">
            Showing data for: <strong>{getFilterLabel()}</strong>
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Refresh Data">
            <IconButton onClick={() => refetch()} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AssessmentIcon />}
            onClick={() => setReportDialogOpen(true)}
            sx={{
              backgroundColor: "#3b82f6",
              "&:hover": {
                backgroundColor: "#2563eb",
              },
            }}
          >
            Generate Report
          </Button>
        </Stack>
      </Box>

      {/* Date Filter */}
      {/* <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <CalendarIcon color="action" />
          <Typography variant="body1" fontWeight={600}>
            Filter Period:
          </Typography>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Time Period</InputLabel>
            <Select value={dateFilter} label="Time Period" onChange={(e) => setDateFilter(e.target.value)}>
              <MenuItem value="today">Today</MenuItem>
              <MenuItem value="30days">Last 30 Days</MenuItem>
              <MenuItem value="month">This Month</MenuItem>
              <MenuItem value="all">All Time</MenuItem>
            </Select>
          </FormControl>
          <Typography variant="body2" color="textSecondary">
            Showing data for: <strong>{getFilterLabel()}</strong>
          </Typography>
        </Stack>
      </Paper> */}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="New Jobs"
            value={displayStats.newJobs}
            icon={<AssignmentIcon sx={{ fontSize: 40 }} />}
            color="primary"
            subtitle={getFilterLabel()}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Processing Jobs"
            value={totalProcessing}
            icon={<ScheduleIcon sx={{ fontSize: 40 }} />}
            color="warning"
            subtitle="In various stages"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Completed Jobs"
            value={displayStats.completedJobs}
            icon={<CheckCircleIcon sx={{ fontSize: 40 }} />}
            color="success"
            subtitle={getFilterLabel()}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Cancelled Jobs"
            value={displayStats.cancelledJobs}
            icon={<AssignmentIcon sx={{ fontSize: 40 }} />}
            color="error"
            subtitle={getFilterLabel()}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Processing Jobs Breakdown */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Processing Jobs Breakdown
            </Typography>
            <List>
              <ListItem>
                <ListItemText primary="Accepted" />
                <Chip label={displayStats.processingJobs.accepted} color="success" size="small" />
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemText primary="Documents Pending" />
                <Chip label={displayStats.processingJobs.documentsPending} color="warning" size="small" />
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemText primary="Tax Pending" />
                <Chip label={displayStats.processingJobs.taxPending} color="info" size="small" />
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemText primary="Verification Pending" />
                <Chip label={displayStats.processingJobs.verificationPending} color="secondary" size="small" />
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemText primary="Client Not Responding" />
                <Chip label={displayStats.processingJobs.clientNotResponding} color="error" size="small" />
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemText primary="Kept on Hold" />
                <Chip label={displayStats.processingJobs.onHold} color="default" size="small" />
              </ListItem>
            </List>
          </Paper>
        </Grid>

        {/* Recent Jobs */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Recent Jobs
            </Typography>
            {displayStats.recentJobs.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography variant="body2" color="textSecondary">
                  No jobs found for the selected period.
                </Typography>
              </Box>
            ) : (
              <List>
                {displayStats.recentJobs.map((job, index) => (
                  <Box key={job.JobId}>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemText
                        primary={
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <Typography variant="subtitle2">{job.JobId}</Typography>
                            <Chip 
                              label={job.subStatus ? getSubStatusLabel(job.subStatus) : job.status} 
                              color={getStatusColor(job.subStatus || job.status)} 
                              size="small" 
                            />
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" color="textSecondary">
                              {job.clientName || job.client} • {job.jobType || job.type}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              Due: {new Date(job.dueDate).toLocaleDateString()}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < displayStats.recentJobs.length - 1 && <Divider />}
                  </Box>
                ))}
              </List>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Report Generator Dialog */}
      <ReportGenerator open={reportDialogOpen} onClose={() => setReportDialogOpen(false)} />
    </Box>
  )
}

export default NormalDashboard